import { angleTo, clamp01, dist } from '../../core/math';
import type { Actor } from '../../sim/actor';
import type { ClassDef, IdentityController } from '../../sim/registry';
import { scaleShape } from '../../sim/shapes';
import { type Mod, T, tripod } from '../../sim/skills';
import type { AreaSpec, HitSpec, ProjectileSpec, ResolvedSkill, SkillAction, SkillDef, SkillStage, StatusApply } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import { applyStatus, removeStatus } from '../../sim/statusSystem';
import type { World } from '../../sim/world';
import { classBaseStats } from '../balance';
import { area, basicAttack, circle, cone, hit, hits, identityOp, invuln, move, proj, rect, self, skill, stage } from './kit';

/**
 * ARSENALIST — tri-weapon gunner (stance-swapper archetype).
 * Twin Sidearms for mobility, a Scattergun for close bursts and a Longrifle
 * for range. Every skill belongs to one weapon; casting another weapon's
 * skill swaps automatically, and entering a stance grants its 9 s buff.
 */

const HUE = 0xffb050;
const EMBER = 0xff7a3a;

const STANCES = ['sidearms', 'scattergun', 'longrifle'] as const;
type Stance = (typeof STANCES)[number];
const STANCE_LABEL = ['Sidearms', 'Scattergun', 'Longrifle'] as const;
const STANCE_BUFF = ['ars_sidearms', 'ars_scattergun', 'ars_longrifle'] as const;
const BUFF_TIME = 9;
const SWAP_TIME = 0.25;
const SWAP_CD = 1;
const DUEL_RANGE = 10;
/** Ranging Shot: no bonus inside RANGING_NEAR, full bonus from RANGING_FAR. */
const RANGING_NEAR = 4;
const RANGING_FAR = 18;
/** Marksman's Trinity: extra damage on a direct (reticle centre) hit. */
const DIRECT_BONUS = 0.5;

registerStatus({ id: 'ars_sidearms', name: 'Sidearms Rhythm', kind: 'buff', icon: 'ars_sidearms', description: 'Attack speed +15%, move speed +10%.', stacking: 'refresh', mods: { atkSpeedPct: 0.15, moveSpeedPct: 0.1 }, color: 0xffd080 });
registerStatus({ id: 'ars_scattergun', name: 'Scattergun Fervor', kind: 'buff', icon: 'ars_scattergun', description: 'Crit rate +10%, damage +20%.', stacking: 'refresh', mods: { critRate: 0.1, dmgPct: 0.2 }, color: 0xff9040 });
registerStatus({ id: 'ars_longrifle', name: 'Longrifle Focus', kind: 'buff', icon: 'ars_longrifle', description: 'Damage +25%.', stacking: 'refresh', mods: { dmgPct: 0.25 }, color: 0xffe0a0 });
registerStatus({ id: 'ars_duel', name: 'Called Out', kind: 'debuff', icon: 'ars_duel', description: 'Move speed -80%, attack speed -40%.', stacking: 'refresh', mods: { moveSpeedPct: -0.8, atkSpeedPct: -0.4 }, cleansable: true, color: 0xffc040 });
registerStatus({ id: 'ars_focus', name: "Hunter's Focus", kind: 'buff', icon: 'ars_focus', description: 'Crit rate +4% per stack.', stacking: 'stack', maxStacks: 3, mods: { critRate: 0.04 }, color: 0xfff0b0 });

const BURN = (duration: number, value = 0.3): StatusApply => ({ id: 'burn', duration, value });
const BLEED: StatusApply = { id: 'bleed', duration: 4, value: 0.2 };
/** Lingering fields tick for damage only: no stagger or destruction per tick. */
const FIELD: Partial<HitSpec> = { stagger: 'none', weakPoint: 0 };

type ShotOpts = Omit<Partial<ProjectileSpec>, 'hit'> & { hit?: Partial<HitSpec> };
/** A fast, small round (pistol or rifle). */
const bullet = (coef: number, o: ShotOpts = {}): ProjectileSpec => {
  const { hit: h, ...rest } = o;
  const radius = rest.radius ?? 0.35;
  return { speed: 48, range: 12, ...rest, radius, hit: { shape: circle(radius), coef, ...h } };
};

// ------------------------------------------------------------------ Sidearms

const WHEEL_TICK = 2 / 15;

const ricochetWheel = skill({
  id: 'ars_wheel', name: 'Ricochet Wheel', hue: HUE, icon: 'spiral', requires: 'sidearms',
  description: 'Sidearms. Fling a spinning pistol to a point (up to 8 m); it ricochets through a 2.5 m area 15 times over 2 s. Foes hit suffer +10% critical hits from your party for 6 s.',
  castType: 'point', cooldown: 8, mana: 45, maxRange: 8, synergy: { id: 'marked', duration: 6 },
  stages: [stage('Attack2', 0.4, [area(0.15, { shape: circle(2.5), anchor: 'point', delay: 0.1, duration: WHEEL_TICK * 14.5, tickInterval: WHEEL_TICK, telegraph: 'none', hit: { shape: circle(2.5), coef: 0.25, reaction: { kind: 'flinch' } } })])],
});

const glideStage = (): SkillStage =>
  stage('Spin', 0.45, [move(0, 6, 0.3, { ignoreCollision: true }), hits(0.06, 3, 0.1, circle(3), 1.5, { reaction: { kind: 'flinch' } })], { cancelAt: 0.34 });

const glideVolley = skill({
  id: 'ars_glide', name: 'Glide Volley', hue: HUE, icon: 'dash', requires: 'sidearms',
  description: 'Sidearms. Glide 6 m toward the cursor through foes, firing in every direction (3 m). Press again within 1.5 s to glide once more.',
  castType: 'combo', cooldown: 6, mana: 40, positional: 'back', superArmor: 'paralysis', followWindow: 1.5,
  stages: [glideStage(), glideStage()],
});

const quickdrawKick = skill({
  id: 'ars_quickdraw', name: 'Quickdraw Kick', hue: HUE, icon: 'fist', requires: 'sidearms',
  description: 'Sidearms. Three inputs: a quickdraw shot, a three-round burst, then a knock-back kick and rapid fire. Re-aim on every input. Counters blue-glowing enemies.',
  castType: 'combo', cooldown: 12, mana: 60, positional: 'back', superArmor: 'paralysis', counter: true, followWindow: 0.9,
  stages: [
    stage('Attack1', 0.35, [proj(0.1, bullet(1.2, { speed: 55, range: 7 }))]),
    stage('Attack2', 0.4, [proj(0.1, bullet(0.4, { range: 6, count: 3, spreadDeg: 30 }))]),
    stage('Kick', 0.7, [
      hit(0.18, cone(2.6, 100), 1.6, { reaction: { kind: 'knockback', power: 2.5 }, vfx: 'heavy' }),
      proj(0.4, bullet(0.27, { range: 7 })),
      proj(0.46, bullet(0.27, { range: 7 })),
      proj(0.52, bullet(0.26, { range: 7 })),
    ]),
  ],
});

// ------------------------------------------------------------------ Scattergun

const doubleBuck = skill({
  id: 'ars_buck', name: 'Double Buck', hue: HUE, requires: 'scattergun',
  description: 'Scattergun. Three inputs: two point-blank blasts (6 m cone), then pump a fresh shell for a heavier, knock-back blast.',
  castType: 'combo', cooldown: 30, mana: 100, positional: 'back', superArmor: 'paralysis', stagger: 'midHigh', weakPoint: 1, followWindow: 0.8,
  stages: [
    stage('Attack1', 0.4, [hit(0.12, cone(6, 60), 3, { reaction: { kind: 'knockback', power: 1 } })]),
    stage('Attack2', 0.4, [hit(0.12, cone(6, 60), 3, { reaction: { kind: 'knockback', power: 1 } })]),
    stage('Attack3', 0.65, [hit(0.3, cone(6, 60), 4.5, { reaction: { kind: 'knockback', power: 2.5 }, vfx: 'heavy' })]),
  ],
});

const breacherSlug = skill({
  id: 'ars_slug', name: 'Breacher Slug', hue: HUE, icon: 'impact', requires: 'scattergun',
  description: 'Scattergun. Fire an explosive slug that blasts a 7 m lane and launches foes. Back and head attacks both count. Counters blue-glowing enemies.',
  castType: 'normal', cooldown: 36, mana: 110, positional: 'both', superArmor: 'paralysis', counter: true, stagger: 'high', weakPoint: 1,
  stages: [stage('Shoot', 0.7, [hit(0.25, rect(7, 2.5), 13, { reaction: { kind: 'launch', power: 1, duration: 1 }, vfx: 'heavy' })])],
});

// ------------------------------------------------------------------ Longrifle

/** One aimed rifle shot: re-aims at the cursor, strikes a 2 m reticle, direct hits deal +50%. */
const trinityShot = (coef: number): SkillStage =>
  stage('Shoot', 0.45, [
    identityOp(0, 'reaim'),
    hit(0.1, circle(2), coef, { anchor: 'point', reaction: { kind: 'flinch' } }),
    hit(0.1, circle(0.4), coef * DIRECT_BONUS, { anchor: 'point', maxTargets: 1, stagger: 'none', weakPoint: 0 }),
  ]);

const marksmansTrinity = skill({
  id: 'ars_trinity', name: "Marksman's Trinity", hue: HUE, icon: 'lance', requires: 'longrifle',
  description: 'Longrifle. Shoulder the rifle for up to three aimed shots (press again within 2.5 s), each striking a 2 m reticle anywhere within 20 m. A direct hit on the centre deals +50%.',
  castType: 'combo', cooldown: 36, mana: 110, maxRange: 20, superArmor: 'paralysis', stagger: 'midHigh', followWindow: 2.5,
  stages: [trinityShot(4.3), trinityShot(4.3), trinityShot(4.3)],
});

const incendiaryRound = skill({
  id: 'ars_incendiary', name: 'Incendiary Round', hue: EMBER, element: 'fire', requires: 'longrifle',
  description: 'Longrifle. Fire a blazing round that pierces everything along 20 m, launching foes and leaving a burning trail for 2.4 s.',
  castType: 'normal', cooldown: 27, mana: 95, superArmor: 'paralysis', stagger: 'midHigh',
  stages: [
    stage('Shoot', 0.7, [
      proj(0.3, { speed: 36, range: 20, radius: 0.75, pierce: true, hit: { shape: circle(0.75), coef: 8.5, reaction: { kind: 'launch', power: 0.4, duration: 0.9 } } }),
      area(0.3, { shape: rect(20, 1.5), anchor: 'self', delay: 0.3, duration: 2.2, tickInterval: 0.4, telegraph: 'none', hit: { shape: rect(20, 1.5), coef: 0.25, ...FIELD } }),
    ]),
  ],
});

const perfectAim = skill({
  id: 'ars_perfect', name: 'Perfect Aim', hue: HUE, icon: 'charge', requires: 'longrifle',
  description: 'Longrifle. Hold to steady your aim, then release to fire a high-calibre round through a 22 m line that knocks foes back. Releasing in the Perfect Zone doubles the damage.',
  castType: 'holding', cooldown: 30, mana: 100, stagger: 'mid', weakPoint: 1,
  stages: [],
  holding: {
    maxHold: 2, tickInterval: 1, tick: [], anim: 'Channel', perfectZone: [1.45, 1.75], perfectMult: 2,
    finisher: stage('Shoot', 0.45, [proj(0.05, { speed: 60, range: 22, radius: 1, pierce: true, hit: { shape: circle(1), coef: 7, reaction: { kind: 'knockback', power: 2 } } })]),
  },
});

// ------------------------------------------------------------------ apex art, movement, basic

const duelShots: SkillAction[] = Array.from({ length: 6 }, (_, i) => {
  const at = 2.1 + i * 0.12;
  return [identityOp(at, 'duelAim'), proj(at + 0.005, bullet(5, { speed: 70, range: 14, radius: 0.45, homing: 0.6, hit: { stagger: 'low', weakPoint: 1, reaction: { kind: 'flinch' } } }))];
}).flat();

const highNoon = skill({
  id: 'ars_highnoon', name: 'High Noon', hue: 0xffd060, kind: 'awakening',
  description: 'APEX ART. Call out the nearest foe within 10 m: it is slowed (move speed -80%, attack speed -40%) for 3.5 s. Two seconds later, empty a six-shot revolver into it.',
  castType: 'normal', cooldown: 300, mana: 0, superArmor: 'full', stagger: 'midHigh', weakPoint: 2,
  stages: [
    stage('Shoot', 2.95, [
      invuln(0, 0.6),
      identityOp(0.05, 'duelAim'),
      hit(0.15, circle(DUEL_RANGE), 2, { maxTargets: 1, status: [{ id: 'ars_duel', duration: 3.5 }], stagger: 'none', weakPoint: 0, reaction: { kind: 'flinch' } }),
      ...duelShots,
    ], { cancelAt: 2.85 }),
  ],
});

const slideTumble = skill({
  id: 'ars_dash', name: 'Slide & Tumble', hue: HUE, icon: 'dash', kind: 'dash',
  description: 'Slide 4 m toward the cursor; press again within 1 s to tumble a further 3 m. Immune to push and crowd control; briefly invulnerable.',
  castType: 'combo', cooldown: 8, mana: 0, superArmor: 'full', followWindow: 1,
  stages: [
    stage('Dash', 0.3, [invuln(0, 0.22), move(0, 4, 0.26, { ignoreCollision: true })], { cancelAt: 0.24 }),
    stage('Leap', 0.32, [invuln(0, 0.22), move(0, 3, 0.26, { ignoreCollision: true })], { cancelAt: 0.26 }),
  ],
});

const basicShot = (coef: number): ProjectileSpec => bullet(coef, { speed: 44, range: 13, homing: 0.25, hit: { reaction: { kind: 'flinch' } } });
const twinSidearms: SkillDef = (() => {
  const b = basicAttack('ars_basic', 'Twin Sidearms', HUE, { coefs: [0.42, 0.42, 0.8], ranged: basicShot(0.42) });
  // The finisher is a three-round flurry from both pistols.
  const last = b.stages[2];
  if (last) last.actions = [0.14, 0.22, 0.3].map((at) => proj(at, basicShot(0.8 / 3)));
  return { ...b, description: 'Alternating pistol shots, then a three-round flurry. Costs no mana.' };
})();

// Identity skills (Z/X): manual weapon swaps.
const swapSkill = (id: string, name: string, dir: 1 | -1): SkillDef =>
  skill({
    id, name, hue: HUE, icon: 'buff', kind: 'identity',
    description: `Switch to the ${dir > 0 ? 'next' : 'previous'} weapon (Sidearms → Scattergun → Longrifle) in 0.25 s. Cancels the recovery of your current skill. Entering a stance grants its buff for 9 s.`,
    castType: 'normal', cooldown: SWAP_CD, mana: 0,
    stages: [stage('Buff', SWAP_TIME, [identityOp(0.02, 'swap', dir)], { cancelAt: SWAP_TIME })],
  });
const swapPrev = swapSkill('ars_swap_prev', 'Weapon Swap (Back)', -1);
const swapNext = swapSkill('ars_swap_next', 'Weapon Swap', 1);

/** Auto-swap played before a skill of another stance (the skill's `preStage`). */
const AUTO_SWAP: readonly SkillStage[] = STANCES.map((_, i) => stage('Buff', SWAP_TIME, [identityOp(0.02, 'stance', i)], { cancelAt: SWAP_TIME }));

// ------------------------------------------------------------------ tripod helpers

type HitAction = Extract<SkillAction, { kind: 'hit' }>;
type AreaAction = Extract<SkillAction, { kind: 'area' }>;
type ProjAction = Extract<SkillAction, { kind: 'projectile' }>;
const isHit = (a: SkillAction): a is HitAction => a.kind === 'hit';
const isArea = (a: SkillAction): a is AreaAction => a.kind === 'area';
const isProj = (a: SkillAction): a is ProjAction => a.kind === 'projectile';
/** Marksman's Trinity centre hit (single-target reticle bonus). */
const isDirect = (h: HitSpec): boolean => h.maxTargets === 1 && h.anchor === 'point';

function eachAction(s: ResolvedSkill, fn: (a: SkillAction) => void): void {
  for (const st of s.stages) st.actions.forEach(fn);
  s.holding?.finisher?.actions.forEach(fn);
  s.holding?.tick.forEach(fn);
}

function eachArea(s: ResolvedSkill, fn: (a: AreaSpec) => void): void {
  eachAction(s, (a) => {
    if (a.kind === 'area') fn(a.area);
  });
}

/** "Area +X%" that also scales ground-effect telegraphs, not just their hit shapes. */
const aoeAll = (pct: number): Mod => (s) => {
  T.aoe(pct)(s);
  const k = 1 + pct;
  eachArea(s, (ar) => {
    ar.shape = ar.shape.kind === 'rect' ? { ...ar.shape, width: ar.shape.width * k } : scaleShape(ar.shape, k);
  });
};

const addTag = (t: string): Mod => (s) => {
  s.tags = [...(s.tags ?? []), t];
};

/** Delays a stage's actions by `d` seconds and runs `before` first (e.g. a step). */
function prependToStage(st: SkillStage | undefined, d: number, before: SkillAction): void {
  if (!st) return;
  for (const a of st.actions) a.at += d;
  st.actions.unshift(before);
  st.duration += d;
  if (st.cancelAt !== undefined) st.cancelAt += d;
}

const growingBite = (s: ResolvedSkill): void => {
  const st = s.stages[0];
  const i = st ? st.actions.findIndex(isArea) : -1;
  const base = st?.actions[i];
  if (!st || !base || !isArea(base)) return;
  // Five bursts of three ricochets, each 15% stronger than the last.
  const bursts = [0, 1, 2, 3, 4].map((k) =>
    area(base.at, { ...base.area, delay: (base.area.delay ?? 0) + k * 3 * WHEEL_TICK, duration: WHEEL_TICK * 2.5, hit: { ...base.area.hit, coef: base.area.hit.coef * (1 + 0.15 * k) } }),
  );
  st.actions.splice(i, 1, ...bursts);
};

const bullseyeCore = (s: ResolvedSkill): void => {
  const st = s.stages[0];
  const base = st?.actions.find(isArea);
  if (!st || !base) return;
  const core = circle(0.9);
  st.actions.push(area(base.at, { ...base.area, shape: core, hit: { ...base.area.hit, shape: core, coef: base.area.hit.coef * 0.7 } }));
};

const extraRounds = (s: ResolvedSkill): void => {
  for (const st of s.stages) {
    for (const a of st.actions) {
      if (a.kind !== 'hit' || (a.hit.count ?? 1) <= 1) continue;
      const n = a.hit.count ?? 1;
      a.hit = { ...a.hit, count: n + 2, coef: (a.hit.coef * (n + 2)) / n, interval: Math.min(a.hit.interval ?? 0.1, 0.06) };
    }
  }
};

const antiAirKick = (s: ResolvedSkill): void => {
  const kick = s.stages[2]?.actions.find(isHit);
  if (kick) kick.hit = { ...kick.hit, reaction: { kind: 'launch', power: 0.5, duration: 1 } };
};

const concussionRounds = (s: ResolvedSkill): void => {
  s.stages.forEach((st, i) => {
    for (const a of st.actions) {
      if (a.kind === 'projectile') a.proj = { ...a.proj, hit: { ...a.proj.hit, stagger: 'low' } };
      else if (a.kind === 'hit' && i === 2) a.hit = { ...a.hit, weakPoint: Math.max(1, a.hit.weakPoint ?? 0) };
    }
  });
};

const stunner = (s: ResolvedSkill): void => {
  const shot = s.stages[0]?.actions.find(isProj);
  if (shot) shot.proj = { ...shot.proj, hit: { ...shot.proj.hit, status: [...(shot.proj.hit.status ?? []), { id: 'stun', duration: 3 }] } };
};

const finisherRound = (s: ResolvedSkill): void => {
  const st = s.stages[2];
  const rapid = st?.actions.filter(isProj) ?? [];
  const first = rapid[0];
  if (!st || !first) return;
  const total = rapid.reduce((sum, a) => sum + a.proj.hit.coef, 0);
  st.actions = st.actions.filter((a) => a.kind !== 'projectile');
  st.actions.push(proj(first.at, { ...first.proj, speed: 60, radius: 0.6, pierce: true, hit: { ...first.proj.hit, shape: circle(0.6), coef: total * 3.9, reaction: { kind: 'knockback', power: 2 } } }));
};

const advance = (s: ResolvedSkill): void => {
  for (const st of s.stages) st.actions.unshift(move(0, 1.35, 0.15));
};

const lastBlast = (s: ResolvedSkill): { st: SkillStage; blast: HitAction } | null => {
  const st = s.stages[s.stages.length - 1];
  const blast = st?.actions.find(isHit);
  return st && blast ? { st, blast } : null;
};

const tripleTap = (s: ResolvedSkill): void => {
  const last = lastBlast(s);
  if (!last) return;
  const { st, blast } = last;
  // Three shells: the first two pin foes in place, the third blows them away.
  const shell = blast.hit.coef * (3.9 / 3);
  blast.hit = { ...blast.hit, coef: shell, reaction: { kind: 'flinch' } };
  st.actions.push({ at: blast.at + 0.1, kind: 'hit', hit: { ...blast.hit } }, { at: blast.at + 0.2, kind: 'hit', hit: { ...blast.hit, reaction: { kind: 'knockback', power: 3.5 } } });
  st.duration = Math.max(st.duration, blast.at + 0.45);
};

const recoilSpread = (s: ResolvedSkill): void => {
  const last = lastBlast(s);
  if (!last) return;
  const { st, blast } = last;
  const r = blast.hit.shape.kind === 'cone' ? blast.hit.shape.radius : 6;
  blast.hit = { ...blast.hit, shape: cone(r, 90), coef: blast.hit.coef * 2.9 };
  st.actions.push(move(blast.at + 0.02, -3, 0.2));
  st.duration = Math.max(st.duration, blast.at + 0.3);
};

const firestorm = (s: ResolvedSkill): void => {
  const st = s.stages[0];
  const slug = st?.actions.find(isHit);
  if (!st || !slug) return;
  const lane = slug.hit.shape;
  st.actions.push(area(slug.at, { shape: lane, anchor: 'self', delay: 0.05, duration: 4.75, tickInterval: 0.5, telegraph: 'none', hit: { shape: lane, coef: 0.3, element: 'fire', ...FIELD } }));
};

const higherLaunch = (s: ResolvedSkill): void => {
  eachAction(s, (a) => {
    if (a.kind === 'hit' && a.hit.reaction?.kind === 'launch') a.hit = { ...a.hit, reaction: { ...a.hit.reaction, power: 1.5, duration: 1.4 } };
  });
};

/** Splits the slug into `n` slugs fired in quick succession (same total damage). */
const multiSlug = (n: number, extraWeakPoint: number): Mod => (s) => {
  const st = s.stages[0];
  const slug = st?.actions.find(isHit);
  if (!st || !slug) return;
  const wp = extraWeakPoint > 0 ? (slug.hit.weakPoint ?? s.weakPoint ?? 0) + extraWeakPoint : slug.hit.weakPoint;
  slug.hit = { ...slug.hit, coef: slug.hit.coef / n, ...(wp !== undefined ? { weakPoint: wp } : {}) };
  for (let k = 1; k < n; k++) st.actions.push({ at: slug.at + k * 0.18, kind: 'hit', hit: { ...slug.hit } });
  st.duration = Math.max(st.duration, slug.at + n * 0.18 + 0.2);
};

const extendedMag = (s: ResolvedSkill): void => {
  const shot = s.stages[0];
  if (!shot) return;
  while (s.stages.length < 7) s.stages.push(structuredClone(shot));
  s.stages[s.stages.length - 1]?.actions.forEach((a) => {
    if (a.kind === 'hit') a.hit = { ...a.hit, coef: a.hit.coef * 2.5 };
  });
};

const hemorrhage = (s: ResolvedSkill): void => {
  const third = s.stages[2]?.actions.find((a): a is HitAction => a.kind === 'hit' && !isDirect(a.hit));
  if (third) third.hit = { ...third.hit, status: [...(third.hit.status ?? []), { id: 'stun', duration: 4 }] };
};

const semiAuto = (s: ResolvedSkill): void => {
  const shotHits = s.stages[0]?.actions.filter(isHit) ?? [];
  const actions: SkillAction[] = [];
  for (const at of [0.15, 0.5, 0.85]) for (const h of shotHits) actions.push({ at, kind: 'hit', hit: { ...h.hit } });
  s.castType = 'point';
  s.followWindow = undefined;
  s.stages = [stage('Shoot', 1.1, actions)];
};

const centerMass = (s: ResolvedSkill): void => {
  eachAction(s, (a) => {
    if (a.kind === 'hit' && isDirect(a.hit)) a.hit = { ...a.hit, coef: a.hit.coef * (1.2 / DIRECT_BONUS) };
  });
};

const quickAim = (s: ResolvedSkill): void => {
  const st = s.stages[0];
  if (!st || st.actions.length === 0) return;
  const windup = Math.min(...st.actions.map((a) => a.at));
  const cut = windup * 0.62;
  for (const a of st.actions) a.at = Math.max(0, a.at - cut);
  st.duration = Math.max(0.2, st.duration - cut);
};

const hotLoad = (s: ResolvedSkill): void => {
  eachAction(s, (a) => {
    if (a.kind === 'projectile') a.proj = { ...a.proj, speed: a.proj.speed * 1.41, homing: 0.5 };
  });
};

const whirlRound = (s: ResolvedSkill): void => {
  const st = s.stages[0];
  if (!st) return;
  const trail = st.actions.find(isArea);
  const trailTotal = trail ? trail.area.hit.coef * 6 : 1.5;
  st.actions = st.actions.filter((a) => a !== trail);
  const at = st.actions.find(isProj)?.at ?? 0.3;
  const vortex = circle(3.5);
  s.maxRange = 20;
  st.actions.push(area(at, { shape: vortex, anchor: 'point', delay: 0.35, duration: 2.75, tickInterval: 0.5, pull: 3.5, telegraph: 'none', vfx: 'pillar', hit: { shape: vortex, coef: (trailTotal * 4.5) / 6, ...FIELD, reaction: { kind: 'flinch' } } }));
};

const backblast = (s: ResolvedSkill): void => {
  eachAction(s, (a) => {
    if (a.kind === 'projectile') a.proj = { ...a.proj, pierce: false, explode: { ...a.proj.hit, shape: rect(6, 3), vfx: 'heavy' } };
  });
};

const shiftZone = (lo: number, hi: number): Mod => (s) => {
  const pz = s.holding?.perfectZone;
  if (s.holding && pz) s.holding.perfectZone = [pz[0] + lo, pz[1] + hi];
};

const autoAim = (s: ResolvedSkill): void => {
  const h = s.holding;
  if (!h) return;
  s.castType = 'casting';
  s.castTime = 1;
  s.stages = h.finisher ? [h.finisher] : [];
  s.dmgMult *= h.perfectMult ?? 1;
  s.holding = undefined;
};

const magnum = (s: ResolvedSkill): void => {
  if (s.holding) s.holding.perfectMult = (s.holding.perfectMult ?? 1) * 1.8;
  else s.dmgMult *= 1.8; // Auto-Aim: every shot is a Perfect shot.
};

// ------------------------------------------------------------------ tripods (facets)

const tripods: ClassDef['tripods'] = {
  ars_wheel: [
    tripod('ars_wheel_t1a', 1, 'Far Throw', 'Range +4 m.', T.range(4)),
    tripod('ars_wheel_t1b', 1, 'Swift Hands', 'Attack speed +36%.', T.atkSpeed(0.36)),
    tripod('ars_wheel_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('ars_wheel_t2a', 2, 'Quicken', 'Move speed +20% for 3 s after throwing.', T.addAction(self(0.05, { id: 'haste', duration: 3, value: 0.2 }), 0)),
    tripod('ars_wheel_t2b', 2, 'Growing Bite', 'Damage grows by 15% every 3 ricochets.', T.custom(growingBite)),
    tripod('ars_wheel_t2c', 2, 'Bullseye Core', 'Foes at the centre of the wheel take 70% more damage.', T.custom(bullseyeCore)),
    tripod('ars_wheel_t3a', 3, 'Gravity Wheel', 'Area +15%; the wheel slowly drags foes to its centre.', aoeAll(0.15), T.custom((s) => eachArea(s, (ar) => {
      ar.pull = 1.5;
    }))),
    tripod('ars_wheel_t3b', 3, 'Twin Charges', 'Store up to 2 throws.', T.charges(2)),
  ],
  ars_glide: [
    tripod('ars_glide_t1a', 1, 'Swift', 'Attack speed +18%.', T.atkSpeed(0.18)),
    tripod('ars_glide_t1b', 1, 'Deadeye', 'Crit rate +15%.', T.crit(0.15)),
    tripod('ars_glide_t1c', 1, 'Mark', 'Foes hit suffer +10% critical hits from your party for 8 s.', T.synergy({ id: 'marked', duration: 8 })),
    tripod('ars_glide_t2a', 2, 'Tight Spread', 'Area -20%; damage +70%.', aoeAll(-0.2), T.dmg(0.7)),
    tripod('ars_glide_t2b', 2, 'Evasive', 'Take 20% less damage while gliding (the rounds you slip past).', T.damageReduction(0.2)),
    tripod('ars_glide_t2c', 2, 'Long Glide', 'Glide distance +3 m.', T.range(3)),
    tripod('ars_glide_t3a', 3, 'Triple Glide', 'Glide up to three times.', T.custom((s) => {
      const first = s.stages[0];
      if (first) s.stages.push(structuredClone(first));
    })),
    tripod('ars_glide_t3b', 3, 'Extra Rounds', 'Fire 2 more rounds per glide.', T.custom(extraRounds)),
  ],
  ars_quickdraw: [
    tripod('ars_quickdraw_t1a', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('ars_quickdraw_t1b', 1, 'Lunge', 'Dash up to 6 m toward the cursor before the first shot.', T.custom((s) => prependToStage(s.stages[0], 0.2, move(0, 6, 0.2, { toPoint: true })))),
    tripod('ars_quickdraw_t1c', 1, 'Evasive', 'Take 10% less damage during the sequence.', T.damageReduction(0.1)),
    tripod('ars_quickdraw_t2a', 2, 'Anti-Air', 'The kick launches foes; +100% damage to airborne foes.', T.custom(antiAirKick), addTag('vsAirborne:1')),
    tripod('ars_quickdraw_t2b', 2, 'Concussion', 'Rounds deal Low stagger; the kick gains Weak Point 1.', T.custom(concussionRounds)),
    tripod('ars_quickdraw_t2c', 2, 'Hollow Points', 'Crit rate +80%.', T.crit(0.8)),
    tripod('ars_quickdraw_t3a', 3, 'Stunner', 'The first shot stuns for 3 s. Cooldown +6 s.', T.custom(stunner), T.cd(6)),
    tripod('ars_quickdraw_t3b', 3, 'Finisher', 'The closing rapid fire becomes one piercing round dealing +290%.', T.custom(finisherRound)),
  ],
  ars_buck: [
    tripod('ars_buck_t1a', 1, 'Point Blank', 'No longer a back attack; damage +40%.', T.noPositional(0.4)),
    tripod('ars_buck_t1b', 1, 'Swift', 'Attack speed +27%.', T.atkSpeed(0.27)),
    tripod('ars_buck_t1c', 1, 'Advance', 'Slide forward while firing (about 4 m in total).', T.custom(advance)),
    tripod('ars_buck_t2a', 2, 'Tenacity', 'Immune to push while firing.', T.superArmor('push')),
    tripod('ars_buck_t2b', 2, 'Heavy Shells', 'Damage +60%.', T.dmg(0.6)),
    tripod('ars_buck_t2c', 2, 'Concussion', 'Stagger +1 tier.', T.stagger(1)),
    tripod('ars_buck_t3a', 3, 'Triple Tap', 'The last blast fires three shells for +290% damage and a harder knock-back.', T.custom(tripleTap)),
    tripod('ars_buck_t3b', 3, 'Recoil Spread', 'The last blast widens to 90° for +190% damage and kicks you 3 m back.', T.custom(recoilSpread)),
  ],
  ars_slug: [
    tripod('ars_slug_t1a', 1, 'Quick Prep', 'Cooldown -13 s.', T.cd(-13)),
    tripod('ars_slug_t1b', 1, 'Incendiary', 'Deals fire damage; foes burn for 3 s.', T.element('fire', BURN(3))),
    tripod('ars_slug_t1c', 1, 'Cryo', 'Deals water damage; foes are slowed by 50% for 5 s.', T.element('water', { id: 'slow', duration: 5, value: -0.5 })),
    tripod('ars_slug_t2a', 2, 'Firestorm', 'The blast leaves the lane burning for 5 s.', T.custom(firestorm)),
    tripod('ars_slug_t2b', 2, 'Overpressure', 'Damage +45%; foes are launched higher.', T.dmg(0.45), T.custom(higherLaunch)),
    tripod('ars_slug_t2c', 2, 'Execute', 'Step 4 m forward before firing; +50% damage to staggered foes.', T.custom((s) => prependToStage(s.stages[0], 0.18, move(0, 4, 0.18))), addTag('vsStaggered:0.5')),
    tripod('ars_slug_t3a', 3, 'Double Slug', 'Fire two slugs, each with Weak Point +1.', multiSlug(2, 1)),
    tripod('ars_slug_t3b', 3, 'Volley', 'Fire three slugs. Damage +120%.', multiSlug(3, 0), T.dmg(1.2)),
  ],
  ars_trinity: [
    tripod('ars_trinity_t1a', 1, 'Quick Aim', 'Shots are fired twice as fast.', T.atkSpeed(1)),
    tripod('ars_trinity_t1b', 1, 'Steady Breath', 'Up to 4 s between shots.', T.custom((s) => {
      s.followWindow = 4;
    })),
    tripod('ars_trinity_t1c', 1, 'Big Bore', 'Reticle area +40%.', T.aoe(0.4)),
    tripod('ars_trinity_t2a', 2, 'Extended Mag', 'Four extra shots; the last one deals +150%.', T.custom(extendedMag)),
    tripod('ars_trinity_t2b', 2, 'Hemorrhage Rounds', 'Shots cause Bleed; the third shot stuns for 4 s.', T.status(BLEED), T.custom(hemorrhage)),
    tripod('ars_trinity_t2c', 2, 'Semi-Auto', 'All three shots fire automatically at the first target point. Damage +75%.', T.custom(semiAuto), T.dmg(0.75)),
    tripod('ars_trinity_t3a', 3, 'Center Mass', 'Direct hits deal +120% instead of +50%.', T.custom(centerMass)),
    tripod('ars_trinity_t3b', 3, "Hunter's Focus", 'Damage +95%; each use grants a Focus stack (crit rate +4%, up to 3, 15 s).', T.dmg(0.95), T.addAction(self(0.05, { id: 'ars_focus', duration: 15 }), 0)),
  ],
  ars_incendiary: [
    tripod('ars_incendiary_t1a', 1, 'Cryo', 'Deals water damage instead; foes are slowed by 50% for 3 s.', T.element('water', { id: 'slow', duration: 3, value: -0.5 })),
    tripod('ars_incendiary_t1b', 1, 'Quick Aim', 'Wind-up -62%.', T.custom(quickAim)),
    tripod('ars_incendiary_t1c', 1, 'No Launch', 'Foes burn for 5 s instead of being launched.', T.reaction({ kind: 'flinch' }), T.status(BURN(5, 0.25))),
    tripod('ars_incendiary_t2a', 2, 'Ranging Shot', `Up to +50% damage the farther the foe (full bonus at ${RANGING_FAR} m).`, addTag('ranging:0.5')),
    tripod('ars_incendiary_t2b', 2, 'Hot Load', 'Round speed +41%; it curves toward foes.', T.custom(hotLoad)),
    tripod('ars_incendiary_t2c', 2, 'Quick Prep', 'Cooldown -11 s.', T.cd(-11)),
    tripod('ars_incendiary_t3a', 3, 'Whirl Round', 'Instead of a trail, a fire tornado erupts at the target point (up to 20 m) and drags foes in; its fire damage is +350%.', T.custom(whirlRound)),
    tripod('ars_incendiary_t3b', 3, 'Backblast', 'No longer pierces: the round detonates through the first foe hit in a 6 m blast. Damage +64%.', T.custom(backblast), T.dmg(0.64)),
  ],
  ars_perfect: [
    tripod('ars_perfect_t1a', 1, 'Bleed', 'The round causes Bleed for 5 s.', T.status({ id: 'bleed', duration: 5, value: 0.3 })),
    tripod('ars_perfect_t1b', 1, 'Stable Stance', 'Immune to push while aiming.', T.superArmor('push')),
    tripod('ars_perfect_t1c', 1, 'Heavy Hit', 'The round knocks foes down for 2 s. Stagger +1 tier.', T.reaction({ kind: 'knockdown', duration: 2, power: 1 }), T.stagger(1)),
    tripod('ars_perfect_t2a', 2, 'Precision', 'A narrower Perfect Zone; crit rate +80%.', shiftZone(0.08, -0.08), T.crit(0.8)),
    tripod('ars_perfect_t2b', 2, 'Auto-Aim', 'Becomes a 1 s cast that always fires a Perfect shot.', T.custom(autoAim)),
    tripod('ars_perfect_t2c', 2, 'Cutthroat', 'Damage +45%; +30% more to foes below 50% HP.', T.dmg(0.45), addTag('vsLowHp:0.3')),
    tripod('ars_perfect_t3a', 3, 'Prepared', 'The Perfect Zone arrives 0.2 s sooner. Damage +60%.', shiftZone(-0.2, -0.2), T.dmg(0.6)),
    tripod('ars_perfect_t3b', 3, 'Magnum', 'Perfect shots deal +80% damage.', T.custom(magnum)),
  ],
};

// ------------------------------------------------------------------ identity

const stanceIndex = (s: SkillDef): number => (s.requires ? STANCES.indexOf(s.requires as Stance) : -1);
const currentStance = (a: Actor): number => a.player?.identity.data.stance ?? 0;
const buffOf = (i: number): string => STANCE_BUFF[i] ?? STANCE_BUFF[0];

/**
 * Skills of the other stances start with a 0.25 s weapon swap (their
 * `preStage`). Idempotent; re-run whenever the stance or skill table changes.
 * Arsenalist facets never set a `preStage` of their own.
 */
function syncStanceStages(a: Actor): void {
  const cur = currentStance(a);
  for (const s of a.skills.values()) {
    const i = stanceIndex(s);
    if (i < 0) continue;
    const want = i === cur ? undefined : AUTO_SWAP[i];
    if (s.preStage !== want) s.preStage = want;
  }
}

function switchStance(world: World, a: Actor, to: number): void {
  const id = a.player!.identity;
  const next = ((Math.round(to) % STANCES.length) + STANCES.length) % STANCES.length;
  if (next === currentStance(a)) return;
  id.data.stance = next;
  id.mode = STANCES[next] ?? STANCES[0];
  STANCE_BUFF.forEach((b, i) => {
    if (i !== next) removeStatus(world, a, b);
  });
  applyStatus(world, a, { id: buffOf(next), duration: BUFF_TIME }, a.id, 0);
  syncStanceStages(a);
}

/** Re-aims the current cast at the cursor (queued combo shots keep their own target). */
function reaim(world: World, a: Actor): void {
  const c = a.cast;
  const aim = world.aimOf(a);
  if (!c || !aim) return;
  let p = { x: aim.x, z: aim.z };
  const max = c.skill.maxRange;
  const d = dist(a.pos, p);
  if (max && d > max) p = { x: a.pos.x + ((p.x - a.pos.x) * max) / d, z: a.pos.z + ((p.z - a.pos.z) * max) / d };
  if (dist(a.pos, p) > 0.05) {
    c.aimAngle = angleTo(a.pos, p);
    a.facing = c.aimAngle;
  }
  c.aimPoint = p;
}

/** High Noon: turn toward the called-out foe (or the nearest one). */
function duelAim(world: World, a: Actor): void {
  const c = a.cast;
  if (!c) return;
  let target: Actor | null = null;
  let best = DUEL_RANGE + 4;
  for (const t of world.aliveActors) {
    if (!t.alive || t.kind === 'npc' || !world.isEnemy(a, t) || !t.hasStatus('ars_duel')) continue;
    const d = dist(a.pos, t.pos);
    if (d < best) {
      best = d;
      target = t;
    }
  }
  target ??= world.nearestEnemy(a, DUEL_RANGE);
  if (!target) return;
  c.aimAngle = angleTo(a.pos, target.pos);
  c.aimPoint = { ...target.pos };
  a.facing = c.aimAngle;
}

const identity: IdentityController = {
  gaugeMax: 100,
  init(a) {
    const id = a.player!.identity;
    id.gauge = 0;
    id.mode = STANCES[0];
    id.timer = 0;
    id.data = { stance: 0 };
    syncStanceStages(a);
  },
  onSkillCast(_w, a, s) {
    // Both directions share one swap cooldown.
    if (s.id === swapPrev.id || s.id === swapNext.id) {
      const cd = a.cooldowns.get(s.id) ?? SWAP_CD;
      a.cooldowns.set(swapPrev.id, cd);
      a.cooldowns.set(swapNext.id, cd);
    }
  },
  onKey(world, a, key) {
    const s = a.skills.get(key === 'Z' ? swapPrev.id : swapNext.id);
    return s ? world.useSkill(a, s, key) : false;
  },
  op(world, a, op, value) {
    if (op === 'stance') switchStance(world, a, value);
    else if (op === 'swap') switchStance(world, a, currentStance(a) + value);
    else if (op === 'reaim') reaim(world, a);
    else if (op === 'duelAim') duelAim(world, a);
  },
  update(_w, a) {
    syncStanceStages(a);
    // The crest ring shows the remaining stance-entry buff.
    const buff = a.getStatus(buffOf(currentStance(a)));
    a.player!.identity.gauge = buff ? (100 * Math.max(0, buff.remaining)) / BUFF_TIME : 0;
  },
  damageMult(a, s, target?: Actor) {
    let m = 1;
    if (!target) return m;
    for (const t of s.tags ?? []) {
      const [k, v] = t.split(':');
      const x = Number(v) || 0;
      if (k === 'vsAirborne' && target.airTimer > 0) m *= 1 + x;
      else if (k === 'vsStaggered' && target.hasStatus('staggered')) m *= 1 + x;
      else if (k === 'vsLowHp' && target.hpPct < 0.5) m *= 1 + x;
      else if (k === 'ranging') m *= 1 + x * clamp01((dist(a.pos, target.pos) - RANGING_NEAR) / (RANGING_FAR - RANGING_NEAR));
    }
    return m;
  },
  describe(a) {
    const i = currentStance(a);
    return {
      label: STANCE_LABEL[i] ?? STANCE_LABEL[0],
      ready: (a.cooldowns.get(swapNext.id) ?? 0) <= 0,
      active: a.hasStatus(buffOf(i)),
      pips: i + 1,
      pipsMax: STANCES.length,
    };
  },
};

export const ARSENALIST: ClassDef = {
  id: 'arsenalist',
  name: 'Arsenalist',
  title: 'Tri-Weapon Gunner',
  archetype: 'Stance swapper',
  role: 'dps',
  description: 'A gunfighter who carries twin sidearms, a scattergun and a longrifle, and always has the right range. Every skill belongs to one weapon; swapping weapons grants a burst of speed, crit or raw damage.',
  hue: HUE,
  weapon: 'pistol',
  model: 'hero_gunner',
  baseStats: (level) => classBaseStats(level, { hp: 0.95, speed: 1.02 }),
  skills: [ricochetWheel, glideVolley, quickdrawKick, doubleBuck, breacherSlug, marksmansTrinity, incendiaryRound, perfectAim],
  defaultBar: ['ars_wheel', 'ars_glide', 'ars_quickdraw', 'ars_buck', 'ars_slug', 'ars_trinity', 'ars_incendiary', 'ars_perfect'],
  basic: twinSidearms,
  dash: slideTumble,
  awakenings: [highNoon],
  identitySkills: [swapPrev, swapNext],
  tripods,
  identity,
  identityName: 'Arsenal',
  identityDescription: 'Three weapons, one stance each: Sidearms (mobility), Scattergun (close burst) and Longrifle (range). Z / X switch to the previous / next weapon in 0.25 s (1 s cooldown; cancels recovery). Casting another weapon\'s skill swaps automatically (+0.25 s). Entering a stance grants its buff for 9 s: Sidearms +15% attack speed and +10% move speed; Scattergun +10% crit and +20% damage; Longrifle +25% damage.',
};
